/**
 * Wave 2.5: Declaration Override Modal
 * 
 * Modal for viewing SORA classification details and overriding GRC via declarations.
 * Shows full provenance data for compliance manager review.
 */
import { useState } from "react";
import { 
  ShieldCheck, AlertTriangle, Info, RefreshCw, 
  Database, Clock, CheckCircle, XCircle, MapPin, Users
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../ui/dialog";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../ui/accordion";
import { toast } from "sonner";
import api from "../../lib/api";

export const DeclarationOverrideModal = ({
  isOpen,
  onClose,
  mission,
  soraClassification,
  onUpdate,
  language = 'cs'
}) => {
  const [saving, setSaving] = useState(false);
  const [recomputing, setRecomputing] = useState(false);
  
  // Local state for declarations
  const [gatheringOfPeople, setGatheringOfPeople] = useState(
    mission?.operator_declarations?.gathering_of_people || false
  );
  const [controlledGroundArea, setControlledGroundArea] = useState(
    mission?.operator_declarations?.controlled_ground_area || false
  );
  const [declarationReason, setDeclarationReason] = useState(
    mission?.operator_declarations?.declaration_reason || ""
  );

  // Translations
  const t = {
    title: language === 'cs' ? 'SORA Klasifikace a Deklarace' : 'SORA Classification & Declarations',
    subtitle: language === 'cs' 
      ? 'Přehled klasifikace a možnost změny deklarací' 
      : 'Classification overview and declaration options',
    currentClassification: language === 'cs' ? 'Aktuální klasifikace' : 'Current Classification',
    dataProvenance: language === 'cs' ? 'Zdroje dat' : 'Data Provenance',
    operatorDeclarations: language === 'cs' ? 'Deklarace operátora' : 'Operator Declarations',
    gatheringLabel: language === 'cs' ? 'Shromáždění osob' : 'Gathering of people',
    gatheringWarning: language === 'cs' 
      ? 'Aktivace vynucuje GRC-7 (nejvyšší riziko)' 
      : 'Activation forces GRC-7 (highest risk)',
    controlledLabel: language === 'cs' ? 'Kontrolovaná pozemní oblast' : 'Controlled ground area',
    controlledBenefit: language === 'cs' 
      ? 'Aktivace umožňuje GRC-1-2 (nejnižší riziko)' 
      : 'Activation allows GRC-1-2 (lowest risk)',
    reasonLabel: language === 'cs' ? 'Odůvodnění' : 'Reason',
    reasonPlaceholder: language === 'cs' ? 'Popište okolnosti...' : 'Describe circumstances...',
    saveAndRecompute: language === 'cs' ? 'Uložit a přepočítat' : 'Save & Recompute',
    saving: language === 'cs' ? 'Ukládám...' : 'Saving...',
    recomputing: language === 'cs' ? 'Přepočítávám...' : 'Recomputing...',
    cancel: language === 'cs' ? 'Zrušit' : 'Cancel',
    success: language === 'cs' ? 'SORA klasifikace aktualizována' : 'SORA classification updated',
    error: language === 'cs' ? 'Chyba při aktualizaci' : 'Error updating',
    populationCategory: language === 'cs' ? 'Kategorie populace' : 'Population category',
    maxDensity: language === 'cs' ? 'Max. hustota' : 'Max density',
    settlements: language === 'cs' ? 'Sídla v oblasti' : 'Settlements in area',
    computedAt: language === 'cs' ? 'Vypočítáno' : 'Computed at',
    noData: language === 'cs' ? 'Údaje nejsou k dispozici' : 'Data not available',
  };

  // Get classification data
  const classification = soraClassification || mission?.sora_classification || {};
  const grcData = classification.grc_data || {};
  const dataSources = classification.data_sources || [];

  const handleSaveAndRecompute = async () => {
    if (!mission?.id) return;
    
    setSaving(true);
    try {
      // 1. Save declarations
      const declarationPayload = {
        gathering_of_people: gatheringOfPeople,
        controlled_ground_area: controlledGroundArea,
        declaration_reason: declarationReason || null,
        declared_at: new Date().toISOString()
      };
      
      await api.put(`/missions/${mission.id}/operator_declarations`, declarationPayload);
      
      // 2. Trigger SORA recompute
      setRecomputing(true);
      await api.post(`/sora/recompute/${mission.id}`);
      
      toast.success(t.success);
      
      // 3. Refresh parent
      if (onUpdate) {
        onUpdate();
      }
      
      onClose();
    } catch (error) {
      console.error("Failed to update declarations:", error);
      toast.error(t.error);
    } finally {
      setSaving(false);
      setRecomputing(false);
    }
  };

  // Get GRC color
  const getGrcColor = (grc) => {
    if (grc <= 2) return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
    if (grc <= 4) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
  };

  // Format population category
  const formatCategory = (category) => {
    if (!category) return t.noData;
    const map = {
      sparsely_populated: language === 'cs' ? 'Řídce osídlená' : 'Sparsely populated',
      sparsely_inhabited: language === 'cs' ? 'Řídce obydlená' : 'Sparsely inhabited',
      populated: language === 'cs' ? 'Obydlená' : 'Populated',
      densely_populated: language === 'cs' ? 'Hustě osídlená' : 'Densely populated',
      gathering_of_people: language === 'cs' ? 'Shromáždění osob' : 'Gathering of people',
      controlled_ground_area: language === 'cs' ? 'Kontrolovaná oblast' : 'Controlled area',
    };
    return map[category] || category;
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5" />
            {t.title}
          </DialogTitle>
          <DialogDescription>
            {t.subtitle}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Current Classification Summary */}
          <div className="p-4 rounded-lg bg-[var(--bg-elevated)] border border-[var(--border-default)]">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <Info className="h-4 w-4" />
              {t.currentClassification}
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <span className="text-xs text-[var(--text-muted)] uppercase">SAIL</span>
                <div className="text-2xl font-bold">{classification.sail || '?'}</div>
              </div>
              <div className="text-center">
                <span className="text-xs text-[var(--text-muted)] uppercase">GRC</span>
                <Badge className={`text-lg px-3 py-1 ${getGrcColor(classification.grc)}`}>
                  {classification.grc || '?'}
                </Badge>
              </div>
              <div className="text-center">
                <span className="text-xs text-[var(--text-muted)] uppercase">ARC</span>
                <div className="text-2xl font-bold uppercase">{classification.arc || '?'}</div>
              </div>
            </div>
            
            {/* Population category and density */}
            {grcData && (
              <div className="mt-4 pt-4 border-t border-[var(--border-default)] grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-[var(--text-muted)]">{t.populationCategory}:</span>
                  <span className="ml-2 font-medium">
                    {formatCategory(grcData.population_category || classification.population_category)}
                  </span>
                </div>
                <div>
                  <span className="text-[var(--text-muted)]">{t.maxDensity}:</span>
                  <span className="ml-2 font-medium">
                    {grcData.max_density_per_km2 
                      ? `${Math.round(grcData.max_density_per_km2)} ppl/km²`
                      : t.noData}
                  </span>
                </div>
              </div>
            )}
            
            {classification.computed_at && (
              <p className="text-xs text-[var(--text-muted)] mt-3">
                {t.computedAt}: {new Date(classification.computed_at).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-GB')}
              </p>
            )}
          </div>

          {/* Accordion for details */}
          <Accordion type="single" collapsible className="w-full">
            {/* Data Provenance */}
            <AccordionItem value="provenance">
              <AccordionTrigger className="text-sm">
                <div className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  {t.dataProvenance}
                  <Badge variant="outline" className="ml-2">
                    {dataSources.length} {language === 'cs' ? 'zdrojů' : 'sources'}
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <div className="space-y-2 pl-6">
                  {dataSources.length > 0 ? dataSources.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm p-2 rounded bg-[var(--bg-elevated)]">
                      <div>
                        <span className="font-medium">{source.name}</span>
                        {source.version && (
                          <span className="text-[var(--text-muted)] ml-2">v{source.version}</span>
                        )}
                      </div>
                      {source.resolution && (
                        <Badge variant="outline" className="text-xs">{source.resolution}</Badge>
                      )}
                    </div>
                  )) : (
                    <p className="text-sm text-[var(--text-muted)]">{t.noData}</p>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Settlements */}
            {grcData.settlements && grcData.settlements.length > 0 && (
              <AccordionItem value="settlements">
                <AccordionTrigger className="text-sm">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    {t.settlements}
                    <Badge variant="outline" className="ml-2">
                      {grcData.settlements.length}
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-wrap gap-2 pl-6">
                    {grcData.settlements.map((settlement, idx) => (
                      <Badge key={idx} variant="secondary">
                        {settlement.name || settlement}
                      </Badge>
                    ))}
                  </div>
                </AccordionContent>
              </AccordionItem>
            )}
          </Accordion>

          {/* Operator Declarations Section */}
          <div className="p-4 rounded-lg border border-[var(--border-default)] space-y-4">
            <h4 className="font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              {t.operatorDeclarations}
            </h4>

            {/* Gathering of People */}
            <div className={`p-3 rounded-lg ${
              gatheringOfPeople 
                ? 'bg-red-50 border border-red-200 dark:bg-red-950/30 dark:border-red-800' 
                : 'bg-[var(--bg-elevated)]'
            }`}>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="font-medium">{t.gatheringLabel}</Label>
                  {gatheringOfPeople && (
                    <div className="flex items-center gap-1 text-sm text-red-600">
                      <AlertTriangle className="h-3 w-3" />
                      {t.gatheringWarning}
                    </div>
                  )}
                </div>
                <Switch
                  checked={gatheringOfPeople}
                  onCheckedChange={setGatheringOfPeople}
                  data-testid="modal-gathering-switch"
                />
              </div>
            </div>

            {/* Controlled Ground Area */}
            <div className={`p-3 rounded-lg ${
              controlledGroundArea 
                ? 'bg-green-50 border border-green-200 dark:bg-green-950/30 dark:border-green-800' 
                : 'bg-[var(--bg-elevated)]'
            }`}>
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label className="font-medium">{t.controlledLabel}</Label>
                  {controlledGroundArea && (
                    <div className="flex items-center gap-1 text-sm text-green-600">
                      <CheckCircle className="h-3 w-3" />
                      {t.controlledBenefit}
                    </div>
                  )}
                </div>
                <Switch
                  checked={controlledGroundArea}
                  onCheckedChange={setControlledGroundArea}
                  data-testid="modal-controlled-switch"
                />
              </div>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label className="text-sm">{t.reasonLabel}</Label>
              <Textarea
                value={declarationReason}
                onChange={(e) => setDeclarationReason(e.target.value)}
                placeholder={t.reasonPlaceholder}
                rows={2}
                data-testid="modal-declaration-reason"
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t.cancel}
          </Button>
          <Button onClick={handleSaveAndRecompute} disabled={saving || recomputing}>
            {recomputing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                {t.recomputing}
              </>
            ) : saving ? (
              t.saving
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                {t.saveAndRecompute}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
